import * as m from "@paraglide/messages"
import type { SupportedLocale } from "@playbrain/content-types"
import { Link } from "@tanstack/react-router"
import { ChevronDown } from "lucide-react"
import { useState } from "react"
import { playdataAlternateLocale, playdataNavItems, playdataServiceItems } from "./playdata-nav"

const navLabel = {
  about: (locale: SupportedLocale) => m.playdata_nav_about({}, { locale }),
  contact: (locale: SupportedLocale) => m.playdata_nav_contact({}, { locale }),
  eventReport: (locale: SupportedLocale) => m.playdata_nav_eventReport({}, { locale }),
  news: (locale: SupportedLocale) => m.playdata_nav_news({}, { locale }),
  streamerReport: (locale: SupportedLocale) => m.playdata_nav_streamerReport({}, { locale }),
}

const linkClass = "text-[1.7rem] hover:text-[var(--color-playdata-blue)]"

/**
 * The Services dropdown is Japanese-only, as on the live site: the two report
 * pages it holds have no English version.
 *
 * The live header: 62px dark bar, the wordmark at 136×25, right-aligned nav in
 * Noto Sans 18.76px, and the current locale's flag as a 20px circle — clicking
 * it switches language, as on the original.
 */
export function PlaydataHeader({ locale }: { locale: SupportedLocale }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const otherLocale = playdataAlternateLocale(locale)

  const services = playdataServiceItems.map((item) => (
    <Link
      className={`${linkClass} block whitespace-nowrap px-[1.6rem] py-[0.8rem]`}
      key={item.key}
      onClick={() => {
        setServicesOpen(false)
        setMenuOpen(false)
      }}
      params={{ locale }}
      to={item.to}
    >
      {navLabel[item.key](locale)}
    </Link>
  ))

  return (
    <header className="sticky top-0 z-20 bg-play-black text-play-white">
      <div className="mx-auto flex h-[6.2rem] max-w-[1400px] items-center justify-between px-[2.4rem] md:px-[3.2rem]">
        <Link
          aria-label={m.playdata_nav_home({}, { locale })}
          params={{ locale }}
          to="/$locale/playdata"
        >
          <img
            alt="PLAYDATA"
            className="h-[2.5rem] w-auto"
            height={25}
            src="/images/playdata/brand/wordmark.png"
            width={136}
          />
        </Link>

        <nav
          aria-label={m.playdata_nav_primary({}, { locale })}
          className="hidden items-center gap-[2.4rem] md:flex"
        >
          <Link
            activeProps={{ className: "underline underline-offset-[0.6rem]" }}
            className={linkClass}
            params={{ locale }}
            to={playdataNavItems[0].to}
          >
            {navLabel.about(locale)}
          </Link>

          {locale === "ja" ? (
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                aria-expanded={servicesOpen}
                aria-haspopup="true"
                className={`${linkClass} inline-flex items-center gap-[0.4rem]`}
                onClick={() => setServicesOpen((open) => !open)}
                type="button"
              >
                {m.playdata_nav_services({}, { locale })}
                <ChevronDown
                  aria-hidden="true"
                  size={14}
                />
              </button>
              {servicesOpen ? (
                <div className="absolute top-full right-0 z-30 flex min-w-[24rem] flex-col bg-play-black py-[0.8rem]">
                  {services}
                </div>
              ) : null}
            </div>
          ) : null}

          {playdataNavItems.slice(1).map((item) => (
            <Link
              activeProps={{ className: "underline underline-offset-[0.6rem]" }}
              className={linkClass}
              key={item.key}
              params={{ locale }}
              to={item.to}
            >
              {navLabel[item.key](locale)}
            </Link>
          ))}

          <Link
            aria-label={m.playdata_nav_switchLocale({}, { locale })}
            className="ml-[0.8rem] block h-[2rem] w-[2rem] overflow-hidden rounded-full ring-play-white transition-[box-shadow] duration-150 hover:ring-2"
            hrefLang={otherLocale}
            params={{ locale: otherLocale }}
            to="/$locale/playdata"
          >
            <img
              alt=""
              className="h-full w-full object-cover"
              height={20}
              src={`/images/playdata/brand/flag-${locale}.png`}
              width={20}
            />
          </Link>
        </nav>

        <button
          aria-controls="playdata-mobile-nav"
          aria-expanded={menuOpen}
          className={`${linkClass} md:hidden`}
          onClick={() => setMenuOpen((open) => !open)}
          type="button"
        >
          {menuOpen ? m.playdata_nav_close({}, { locale }) : m.playdata_nav_menu({}, { locale })}
        </button>
      </div>

      {menuOpen ? (
        <nav
          aria-label={m.playdata_nav_primary({}, { locale })}
          className="flex flex-col gap-[1.2rem] border-play-white-30 border-t px-[2.4rem] py-[2rem] md:hidden"
          id="playdata-mobile-nav"
        >
          <Link
            className={`${linkClass} text-[1.8rem]`}
            onClick={() => setMenuOpen(false)}
            params={{ locale }}
            to={playdataNavItems[0].to}
          >
            {navLabel.about(locale)}
          </Link>
          {locale === "ja" ? (
            <>
              <p className="text-[1.3rem] text-play-white-50 uppercase tracking-[0.1em]">
                {m.playdata_nav_services({}, { locale })}
              </p>
              {services}
            </>
          ) : null}
          {playdataNavItems.slice(1).map((item) => (
            <Link
              className={`${linkClass} text-[1.8rem]`}
              key={item.key}
              onClick={() => setMenuOpen(false)}
              params={{ locale }}
              to={item.to}
            >
              {navLabel[item.key](locale)}
            </Link>
          ))}
          <Link
            className={`${linkClass} inline-flex items-center gap-[1rem] text-[1.8rem]`}
            hrefLang={otherLocale}
            onClick={() => setMenuOpen(false)}
            params={{ locale: otherLocale }}
            to="/$locale/playdata"
          >
            <img
              alt=""
              className="h-[2.4rem] w-[2.4rem] rounded-full"
              height={24}
              src={`/images/playdata/brand/flag-${otherLocale}.png`}
              width={24}
            />
            {m.playdata_nav_switchLocale({}, { locale })}
          </Link>
        </nav>
      ) : null}
    </header>
  )
}

export default PlaydataHeader
